import i18next from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { ar } from './locales/ar';
import { de } from './locales/de';
import { en } from './locales/en';
import type { TranslationKey } from './locales/en';
import { es } from './locales/es';
import { fr } from './locales/fr';
import { hi } from './locales/hi';
import { it } from './locales/it';
import { ja } from './locales/ja';
import { nl } from './locales/nl';
import { pl } from './locales/pl';
import { pt } from './locales/pt';
import { zh } from './locales/zh';

import { SUPPORTED_LOCALES, LOCALE_TAGS, normalizeLocale, localeDirection, type SupportedLocale } from '@vendepay/contracts/src/checkout-locale';
export { SUPPORTED_LOCALES, LOCALE_TAGS, normalizeLocale, localeDirection, type SupportedLocale } from '@vendepay/contracts/src/checkout-locale';

export const LOCALE_OPTIONS: ReadonlyArray<{ readonly locale: SupportedLocale; readonly label: string }> = [
  { locale: 'pt', label: 'Português' },
  { locale: 'en', label: 'English' },
  { locale: 'es', label: 'Español' },
  { locale: 'de', label: 'Deutsch' },
  { locale: 'fr', label: 'Français' },
  { locale: 'it', label: 'Italiano' },
  { locale: 'nl', label: 'Nederlands' },
  { locale: 'pl', label: 'Polski' },
  { locale: 'zh', label: '中文' },
  { locale: 'hi', label: 'हिन्दी' },
  { locale: 'ar', label: 'العربية' },
  { locale: 'ja', label: '日本語' },
];

const LOCALE_STORAGE_KEY = 'vendepay:checkout-locale';

const COUNTRY_ALIASES: Readonly<Record<string, string>> = {
  BRA: 'BR', USA: 'US', EUA: 'US', CHL: 'CL', PER: 'PE', COL: 'CO', MEX: 'MX', CAN: 'CA', JPN: 'JP',
  GBR: 'GB', DNK: 'DK', NOR: 'NO', SWE: 'SE', CHE: 'CH', PRY: 'PY', DEU: 'DE', PRT: 'PT', ESP: 'ES',
  FRA: 'FR', ITA: 'IT', BEL: 'BE', NLD: 'NL', FIN: 'FI', IRL: 'IE', AUT: 'AT', POL: 'PL', LUX: 'LU',
  MCO: 'MC', CHN: 'CN', IND: 'IN', ARE: 'AE', SAU: 'SA', EGY: 'EG', MAR: 'MA', DZA: 'DZ',
};

const COUNTRY_LOCALES: Readonly<Record<string, readonly SupportedLocale[]>> = {
  BR: ['pt'], PT: ['pt'],
  US: ['en'], CA: ['en'], GB: ['en'], IE: ['en'], DK: ['en'], NO: ['en'], SE: ['en'], FI: ['en'],
  CL: ['es'], PE: ['es'], CO: ['es'], MX: ['es'], ES: ['es'], PY: ['es'],
  DE: ['de'], AT: ['de'], FR: ['fr'], MC: ['fr'], IT: ['it'], NL: ['nl'], PL: ['pl'], JP: ['ja'],
  BE: ['nl', 'fr', 'de'], CH: ['de', 'fr', 'it'], LU: ['fr', 'de'],
  CN: ['zh'], SG: ['zh', 'en'], IN: ['hi', 'en'],
  AE: ['ar', 'en'], SA: ['ar'], EG: ['ar'], MA: ['ar', 'fr'], DZ: ['ar', 'fr'], QA: ['ar', 'en'],
  KW: ['ar', 'en'], BH: ['ar', 'en'], OM: ['ar', 'en'], JO: ['ar', 'en'], LB: ['ar', 'fr'], IQ: ['ar'],
};

const CURRENCY_LOCALES: Readonly<Record<string, SupportedLocale>> = {
  BRL: 'pt', CLP: 'es', PEN: 'es', COP: 'es', MXN: 'es', PYG: 'es', JPY: 'ja', CNY: 'zh', INR: 'hi',
};

const STRIPE_LOCALES = {
  pt: 'pt-BR', en: 'en', es: 'es-419', de: 'de', fr: 'fr', it: 'it', nl: 'nl', pl: 'pl', zh: 'zh',
  hi: 'en', ar: 'ar', ja: 'ja',
} as const;

const resources = {
  pt: { translation: pt }, en: { translation: en }, es: { translation: es }, de: { translation: de },
  fr: { translation: fr }, it: { translation: it }, nl: { translation: nl }, pl: { translation: pl },
  zh: { translation: zh }, hi: { translation: hi }, ar: { translation: ar }, ja: { translation: ja },
};

export const checkoutI18n = i18next.createInstance();
void checkoutI18n.use(initReactI18next).init({
  resources,
  lng: initialLocale(),
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LOCALES],
  nonExplicitSupportedLngs: true,
  load: 'languageOnly',
  keySeparator: false,
  returnNull: false,
  initAsync: false,
  interpolation: { escapeValue: false },
});

checkoutI18n.on('languageChanged', (language) => updateDocumentLocale(normalizeLocale(language) ?? 'en'));
updateDocumentLocale(currentLocale());

export function tr(key: TranslationKey, values?: Record<string, unknown>): string {
  return checkoutI18n.t(key, values ?? {});
}

export function currentLocale(): SupportedLocale {
  return normalizeLocale(checkoutI18n.resolvedLanguage ?? checkoutI18n.language) ?? 'en';
}

export function currentLocaleTag(): string {
  return LOCALE_TAGS[currentLocale()];
}

export function stripeLocale(): (typeof STRIPE_LOCALES)[SupportedLocale] {
  return STRIPE_LOCALES[currentLocale()];
}

export function resolveCheckoutLocale(input: {
  readonly country?: string | undefined;
  readonly currency?: string | undefined;
  readonly browserLanguages?: readonly string[] | undefined;
}): SupportedLocale {
  const browserLocales = (input.browserLanguages ?? []).flatMap((language) => {
    const locale = normalizeLocale(language);
    return locale === undefined ? [] : [locale];
  });
  const rawCountry = input.country?.trim().toUpperCase();
  const country = rawCountry === undefined ? undefined : COUNTRY_ALIASES[rawCountry] ?? rawCountry;
  const candidates = country === undefined ? undefined : COUNTRY_LOCALES[country];
  if (candidates !== undefined && candidates.length > 0) {
    return browserLocales.find((locale) => candidates.includes(locale)) ?? candidates[0]!;
  }
  const browserLocale = browserLocales[0];
  if (browserLocale !== undefined) {
    return browserLocale;
  }
  const currencyLocale = input.currency === undefined ? undefined : CURRENCY_LOCALES[input.currency.toUpperCase()];
  return currencyLocale ?? 'en';
}

export function useCheckoutLocale(country?: string, currency?: string): SupportedLocale {
  const { i18n } = useTranslation(undefined, { i18n: checkoutI18n });
  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language) ?? 'en';
  useEffect(() => {
    if (readLocaleOverride() !== undefined) return;
    const resolved = resolveCheckoutLocale({ country, currency, browserLanguages: browserLanguages() });
    if (resolved !== currentLocale()) void checkoutI18n.changeLanguage(resolved);
  }, [country, currency]);
  return locale;
}

export function useCurrentLocale(): SupportedLocale {
  const { i18n } = useTranslation(undefined, { i18n: checkoutI18n });
  return normalizeLocale(i18n.resolvedLanguage ?? i18n.language) ?? 'en';
}

export function setCheckoutLocale(locale: SupportedLocale): void {
  try {
    globalThis.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
  }
  void checkoutI18n.changeLanguage(locale);
}

export function clearCheckoutLocaleOverride(): void {
  try {
    globalThis.localStorage?.removeItem(LOCALE_STORAGE_KEY);
  } catch {
  }
}

function initialLocale(): SupportedLocale {
  return readLocaleOverride() ?? resolveCheckoutLocale({ browserLanguages: browserLanguages() });
}

function readLocaleOverride(): SupportedLocale | undefined {
  try {
    return normalizeLocale(globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY) ?? undefined);
  } catch {
    return undefined;
  }
}

function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages.length > 0 ? navigator.languages : [navigator.language];
}

function updateDocumentLocale(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = LOCALE_TAGS[locale];
  document.documentElement.dir = localeDirection(locale);
}
